import localforage from 'localforage';

const STORAGE_KEY = 'companyEntities_v1';

// The company entity data is held as a static JSON file. If the data is not present in the
// local store (on the first run, or if the data has been reset), it is reloaded from the file.
// This means that to change the data, you may need to both change the static data file AND run
// the "Clear Local Data" page under Admin => Advanced settings.
const loadDefaults = async () => {
  try {
    const defaults = require('./_local/_companyEntitiesData.json');
    return defaults || [];
  } catch (e) {
    return [];
  }
};

const read = async () => {
  try {
    const raw = await localforage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = await loadDefaults();
      await localforage.setItem(STORAGE_KEY, defaults);
      return defaults;
    }
    return raw;
  } catch (e) {
    return [];
  }
};

const write = async (data) => {
  await localforage.setItem(STORAGE_KEY, data);
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
export const resetToDefaults = async () => {
  const defaults = await loadDefaults();
  await write(defaults);
  return defaults;
};

export const getAllCompanyEntities = async () => await read();

// Kept for compatibility with previous localDefaultCompanyEntityStore consumers.
export const getAllDefaultCompanyEntities = async () => {
  const data = await read();
  return (data || []).filter(d => d && d.isDefault === true);
};

// Kept for compatibility with previous localDefaultCompanyEntityStore consumers.
export const getByCompanyAndEntity = async (companyId, entityId) => {
  if (companyId === null || companyId === undefined || entityId === null || entityId === undefined) return null;
  const data = await read();
  return data.find(d => d && String(d.companyId) === String(companyId) && String(d.entityId) === String(entityId)) || null;
};

export const getCompanyEntitiesByCompanyId = async (companyId) => {
  const data = await read();
  if (companyId === null || companyId === undefined || companyId === '') return data;
  return data.filter(d => d && (d.companyId === Number(companyId) || String(d.companyId) === String(companyId)));
};

export const addCompanyEntity = async (companyEntity) => {
  const data = await read();
  data.push(companyEntity);
  await write(data);
  return companyEntity;
};

// Kept for compatibility with previous localDefaultCompanyEntityStore consumers.
export const addDefaultCompanyEntity = async (item) => {
  const data = await read();
  data.push(item);
  await write(data);
  return item;
};

// Kept for compatibility with previous localDefaultCompanyEntityStore consumers.
export const getNextEntityId = async () => {
  try {
    const items = await read();
    if (!items || items.length === 0) return 1;
    let max = -Infinity;
    for (const i of items) {
      if (!i) continue;
      const rawId = i.entityId ?? i.id ?? i.entity_id;
      const num = Number(rawId);
      if (!Number.isNaN(num) && isFinite(num)) {
        if (num > max) max = num;
      }
    }
    if (max === -Infinity) return 1;
    return max + 1;
  } catch (e) {
    return 1;
  }
};

export default {
  getAllCompanyEntities,
  getAllDefaultCompanyEntities,
  getByCompanyAndEntity,
  getCompanyEntitiesByCompanyId,
  addCompanyEntity,
  addDefaultCompanyEntity,
  getNextEntityId,
  resetToDefaults,
};
